import { useQuery } from '@tanstack/react-query';
import { companyApi } from '@/lib/api/services/company.api';
import type { Company } from '@/types/company';

export interface UseSoleActiveConceptOptions {
  enabled?: boolean;
  /** Narrow the count to one organization. Omit to count across all of them. */
  organizationId?: number;
}

/**
 * The one active concept, when there is exactly one to be had.
 *
 * A form that asks "which concept?" when only one answer exists is asking a
 * question with no wrong answer, so callers use this to pre-fill the field
 * (T-902). Two or more active concepts — or none — yields `undefined`, and the
 * field stays required and empty.
 *
 * Two rows are fetched rather than one: `meta.total` is what decides, and a
 * page of two keeps the reply small while still leaving the chosen concept in
 * hand when the answer is "exactly one".
 */
export function useSoleActiveConcept({
  enabled = true,
  organizationId,
}: UseSoleActiveConceptOptions = {}) {
  const query = useQuery({
    queryKey: ['sole-active-concept', organizationId ?? null],
    queryFn: () =>
      companyApi.paginate({ page: 1, limit: 2, isActive: true, organizationId }),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  const concept: Company | undefined =
    query.data?.meta.total === 1 ? query.data.data[0] : undefined;

  return { ...query, concept };
}
